// The parameters a material graph EXPOSES — the Unreal-style "Converted to
// Parameter" nodes plus the legacy per-input `expose` map, deduped by
// (type, name). What the content browser's inspector renders and what an edit
// writes back through `setGraphParam`.
//
// A LEAF, and it never belonged with a compiler. It lived in
// the deleted GLSL compiler and was the last reason two editors imported that
// module after it stopped compiling anything a mesh wears. It emits no shader
// source of any kind: it is a walk over the graph's own nodes against the
// `NODE_TYPES` table, so the answer is identical whoever renders the material.

import { NODE_TYPES } from './materialNodes.js'

export function collectExposedParams(graph) {
  const out = []
  const seen = new Set() // dedupe by `${type}:${name}` — same name+type = same param
  for (const n of graph?.nodes ?? []) {
    const def = NODE_TYPES[n.type]
    // Unreal-style parameter nodes (a Constant or Texture Sample "Converted to
    // Parameter"). Texture params carry a textureId; numeric params a value.
    if (n.data?.param) {
      const isTexture = !!def?.hasTexture
      const type = isTexture ? 'texture' : (def?.outputs?.[0]?.type ?? 'float')
      const name = (n.data.paramName != null && String(n.data.paramName).trim()) || 'Param'
      const key = `${type}:${name}`
      if (!seen.has(key)) {
        seen.add(key)
        const value = isTexture
          ? (n.data.textureId ?? null)
          : (n.data.value ?? (type === 'float' ? 0 : [0, 0, 0]))
        out.push({ nodeId: n.id, handle: isTexture ? 'textureId' : 'value', type, name, value })
      }
      continue
    }
    // Legacy per-input expose (existing materials authored before parameters).
    const expose = n.data?.expose
    if (!expose) continue
    for (const [handle, name] of Object.entries(expose)) {
      const inputDef = def?.inputs?.find(i => i.handle === handle)
      const type = inputDef?.type ?? 'float'
      const value = n.data?.[handle] ?? inputDef?.default ?? (type === 'float' ? 0 : [0, 0, 0])
      out.push({ nodeId: n.id, handle, type, name, value })
    }
  }
  return out
}
